/**
 * What was ordered, what arrived, and what is still owed.
 *
 * WHY REJECTED HAS ITS OWN COLUMN
 * -------------------------------
 * Twelve arrive, two are rotten, ten go on the shelf. Rolling those two into
 * "received" loses the fact that anything was wrong with the delivery — and
 * that is the column somebody chasing a supplier over an invoice actually
 * needs. It is also the difference between the kitchen having ten and the
 * paperwork saying twelve.
 *
 * WHY BLANK AND NOUGHT ARE NOT THE SAME
 * -------------------------------------
 * A blank means no delivery has been recorded against the order at all. A
 * nought means one was, and nothing usable came. They lead to different phone
 * calls, and a report that turns the first into the second is telling somebody
 * the supplier delivered nothing when nobody has looked.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatPKR, today, dateKey } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { ReportExport } from './ReportExport.jsx';
import { ReportTabs } from './ReportTabs.jsx';

const startOfMonth = (day) => `${day.slice(0, 7)}-01`;

/** A figure, or a dash where there is nothing to report. */
const orDash = (value) =>
  value === null || value === undefined ? <span className="text-ink-3">—</span> : formatPKR(value);

export default function PurchaseReportPage() {
  const [from, setFrom] = useState(() => startOfMonth(today()));
  const [to, setTo] = useState(today);
  const [by, setBy] = useState('order');

  const query = { start_date: from, end_date: to, group_by: by };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['reports', 'purchases', from, to, by],
    queryFn: () => api.get(withQuery('/reports/purchases', query)),
  });

  function setMonthsAgo(months) {
    const start = new Date();
    start.setMonth(start.getMonth() - months, 1);
    const end = months === 0 ? new Date() : new Date(start.getFullYear(), start.getMonth() + 1, 0);
    setFrom(dateKey(start));
    setTo(dateKey(end));
  }

  const rows = data?.rows ?? [];
  const totals = data?.totals;
  const bySupplier = by === 'supplier';

  return (
    <div className="mx-auto max-w-5xl p-4">
      <ReportTabs />
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold text-xl text-ink">Purchases</h1>
          <p className="text-sm text-ink-3">What was ordered, what arrived, and what is owed</p>
        </div>

        <ReportExport
          path={withQuery('/reports/purchases', query)}
          name={`Purchase report ${from} to ${to}`}
        />
      </div>

      <div className="mb-5 flex flex-wrap items-end gap-2">
        <label className="text-sm">
          <span className="mb-1 block text-xs text-ink-3">From</span>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-10 rounded-lg border border-rule bg-surface-3 px-2.5 text-sm text-ink"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-ink-3">To</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-10 rounded-lg border border-rule bg-surface-3 px-2.5 text-sm text-ink"
          />
        </label>
        <button
          type="button"
          onClick={() => setMonthsAgo(0)}
          className="h-10 rounded-lg border border-rule px-3 text-sm text-ink-2 hover:text-ink"
        >
          This month
        </button>
        <button
          type="button"
          onClick={() => setMonthsAgo(1)}
          className="h-10 rounded-lg border border-rule px-3 text-sm text-ink-2 hover:text-ink"
        >
          Last month
        </button>

        <label className="ml-auto text-sm">
          <span className="mb-1 block text-xs text-ink-3">Show</span>
          <select
            aria-label="How to group"
            value={by}
            onChange={(e) => setBy(e.target.value)}
            className="h-10 rounded-lg border border-rule bg-surface-3 px-2.5 text-sm text-ink"
          >
            <option value="order">Every order</option>
            <option value="supplier">Totals by supplier</option>
          </select>
        </label>
      </div>

      {isLoading && <Spinner label="Adding up the purchases" />}

      {isError && (
        <p role="alert" className="rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {error?.message ?? 'That report could not be produced.'}
        </p>
      )}

      {data && (
        <div className="overflow-x-auto rounded-xl border border-rule">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-rule text-left text-xs text-ink-3">
                {bySupplier ? (
                  <>
                    <th className="px-3 py-2 font-medium">Supplier</th>
                    <th className="px-3 py-2 text-right font-medium">Orders</th>
                    <th className="px-3 py-2 text-right font-medium">Ordered</th>
                  </>
                ) : (
                  <>
                    <th className="px-3 py-2 font-medium">Ordered</th>
                    <th className="px-3 py-2 font-medium">Order</th>
                    <th className="px-3 py-2 font-medium">Supplier</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 text-right font-medium">Value</th>
                    <th className="px-3 py-2 text-right font-medium">Received</th>
                    <th className="px-3 py-2 text-right font-medium">Rejected</th>
                    <th className="px-3 py-2 text-right font-medium">Outstanding</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={bySupplier ? 3 : 8} className="px-3 py-6 text-center text-ink-3">
                    Nothing was ordered in this period.
                  </td>
                </tr>
              )}

              {rows.map((row, index) =>
                bySupplier ? (
                  <tr key={index} className="border-b border-rule/50">
                    <td className="px-3 py-2 text-ink">{row.supplier}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-2">{row.orders}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink">
                      {formatPKR(row.ordered)}
                    </td>
                  </tr>
                ) : (
                  <tr key={index} className="border-b border-rule/50">
                    <td className="px-3 py-2 text-ink-2">{row.ordered_on}</td>
                    <td className="px-3 py-2 text-ink-2">{row.reference}</td>
                    <td className="px-3 py-2 text-ink">{row.supplier}</td>
                    <td className="px-3 py-2 text-ink-2">{row.status}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-2">
                      {formatPKR(row.ordered)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-2">
                      {orDash(row.received)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums ${
                        row.rejected > 0 ? 'text-warn' : 'text-ink-2'
                      }`}
                    >
                      {orDash(row.rejected)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums ${
                        row.outstanding > 0 ? 'text-warn' : 'text-ink-2'
                      }`}
                    >
                      {formatPKR(row.outstanding)}
                    </td>
                  </tr>
                )
              )}
            </tbody>

            {totals && (
              <tfoot>
                <tr className="font-medium">
                  {bySupplier ? (
                    <>
                      <td className="px-3 py-2.5 text-ink">Total</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-ink">
                        {totals.orders}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-ink">
                        {formatPKR(totals.ordered)}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2.5 text-ink" colSpan={4}>
                        Total
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-ink">
                        {formatPKR(totals.ordered)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-ink">
                        {formatPKR(totals.received)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-ink">
                        {formatPKR(totals.rejected)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-ink">
                        {formatPKR(totals.outstanding)}
                      </td>
                    </>
                  )}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {data && !bySupplier && (
        <p className="mt-3 text-sm text-ink-3">
          Received counts what was accepted. Rejected goods are shown separately rather than rolled
          into it — a dash means no delivery has been recorded against the order at all, which is
          not the same as a delivery of nothing.
        </p>
      )}
    </div>
  );
}
